import React, {useEffect, useState} from "react";
import {
    Breadcrumbs, Card, Link, Chip, IconButton, Sheet, Drawer, Input,
    LinearProgress, Radio, RadioGroup, Button, Alert, Modal, ModalDialog
} from "@mui/joy";
import Box from "@mui/joy/Box";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import Typography from "@mui/joy/Typography";
import {
    GetCourseBySlug,
    GetDocumentByCourseSlug,
    ToggleDocumentComplete,
    SubmitQuiz,
} from "../../axios/Axios";
import {
    ArrowRightAltSharp, OpenInNew, Menu as MenuIcon,
    Search as SearchIcon, CheckCircle, Circle,
    PlayCircleOutline, Quiz as QuizIcon
} from "@mui/icons-material";
import {useParams} from "react-router-dom";
import AccordionGroup from '@mui/joy/AccordionGroup';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary, {accordionSummaryClasses} from '@mui/joy/AccordionSummary';
import AddIcon from '@mui/icons-material/Add';
import YouTubeEmbed from "../dashboard-components/YouTubeEmbed";

// Helper function to shuffle an array (Fisher-Yates algorithm)
const shuffleArray = <T, >(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
};

interface QuizAnswer {
    id: number;
    answer_text: string;
    order: number;
}

interface QuizQuestion {
    id: number;
    question_text: string;
    question_type: 'multiple_choice' | 'true_false';
    order: number;
    points: number;
    answers: QuizAnswer[];
    helper_text: string | null;
    correct_answer_id: number;
}

interface Quiz {
    id: number;
    title: string;
    passing_percentage: number;
    time_limit_minutes: number | null;
    questions: QuizQuestion[];
    total_questions: number;
    total_points: number;
}

interface DocumentProgress {
    id: number;
    is_completed: boolean;
    completed_at: string | null;
    completion_method: 'manual' | 'quiz';
}

interface Document {
    slug: string;
    title: string;
    content: string;
    category: string;
    author: string;
    youtube_link?: string;
    links?: { title: string; link: string }[];
    requires_quiz: boolean;
    has_quiz: boolean;
    quiz?: Quiz;
    user_progress?: DocumentProgress | null;
}

interface Course {
    slug: string;
    title: string;
}

export default function KnowledgeBaseCourseDetail() {
    const {slug} = useParams();
    const [loadingData, setLoadingData] = useState(true);
    const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [course, setCourse] = useState<Course | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [scrollProgress, setScrollProgress] = useState(0);

    const [quizModalOpen, setQuizModalOpen] = useState(false);
    const [submittingQuiz, setSubmittingQuiz] = useState(false);
    const [quizResult, setQuizResult] = useState<any>(null);
    const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
    const [quizStartTime, setQuizStartTime] = useState<number | null>(null);
    const [shuffledQuiz, setShuffledQuiz] = useState<Quiz | null>(null); // State for the randomized quiz

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
    const [answerStatus, setAnswerStatus] = useState<'unanswered' | 'correct' | 'incorrect'>('unanswered');
    const [finalAnswers, setFinalAnswers] = useState<Record<number, number>>({});

    const groupedDocuments = documents.reduce((acc, document) => {
        if (!acc[document.category]) {
            acc[document.category] = [];
        }
        acc[document.category].push(document);
        return acc;
    }, {} as Record<string, Document[]>);

    const filteredGroupedDocuments = Object.keys(groupedDocuments).reduce((acc, category) => {
        const filtered = groupedDocuments[category].filter(doc =>
            doc.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (filtered.length > 0) {
            acc[category] = filtered;
        }
        return acc;
    }, {} as Record<string, Document[]>);

    useEffect(() => {
        setLoadingData(true)

        const getCourse = async () => {
            let courseData = await GetCourseBySlug(slug!);
            setCourse(courseData)
        };

        const getDocuments = async () => {
            let documentData = await GetDocumentByCourseSlug(slug!);
            if (documentData.length > 0) {
                setSelectedDocument(documentData[0])
            }
            setDocuments(documentData)
        };
        getDocuments();
        getCourse();
        setLoadingData(false)
    }, [slug])

    useEffect(() => {
        if (!quizModalOpen || timeRemaining === null) return;

        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev === null || prev <= 1) {
                    handleQuizSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [quizModalOpen, timeRemaining]);

    useEffect(() => {
        const handleScroll = (e: Event) => {
            const target = e.target as HTMLElement;
            const scrollTop = target.scrollTop;
            const scrollHeight = target.scrollHeight - target.clientHeight;
            const progress = (scrollTop / scrollHeight) * 100;
            setScrollProgress(Math.min(progress, 100));
        };

        const contentCard = document.getElementById('content-card');
        if (contentCard) {
            contentCard.addEventListener('scroll', handleScroll);
            return () => contentCard.removeEventListener('scroll', handleScroll);
        }
    }, [selectedDocument]);

    const handleDocumentSelect = (document: Document) => {
        setSelectedDocument(document);
        setDrawerOpen(false);
        setScrollProgress(0);
        setQuizResult(null);
    };

    const handleToggleComplete = async () => {
        if (!selectedDocument) return;

        try {
            const result = await ToggleDocumentComplete(selectedDocument.slug);
            setDocuments(prevDocs =>
                prevDocs.map(doc =>
                    doc.slug === selectedDocument.slug
                        ? {...doc, user_progress: result.progress}
                        : doc
                )
            );
            setSelectedDocument(prev =>
                prev ? {...prev, user_progress: result.progress} : null
            );
        } catch (error: any) {
            alert(error.response?.data?.error || 'Failed to toggle completion');
        }
    };

    const handleStartQuiz = () => {
        if (!selectedDocument?.quiz) return;

        // Create a randomized version of the quiz
        const quizToShuffle = JSON.parse(JSON.stringify(selectedDocument.quiz));
        quizToShuffle.questions = shuffleArray(quizToShuffle.questions);
        quizToShuffle.questions.forEach((question: QuizQuestion) => {
            question.answers = shuffleArray(question.answers);
        });
        setShuffledQuiz(quizToShuffle);

        // Reset quiz state
        setFinalAnswers({});
        setCurrentQuestionIndex(0);
        setSelectedAnswer(null);
        setAnswerStatus('unanswered');
        setQuizResult(null);
        setQuizModalOpen(true);
        setQuizStartTime(Date.now());

        if (selectedDocument.quiz.time_limit_minutes) {
            setTimeRemaining(selectedDocument.quiz.time_limit_minutes * 60);
        } else {
            setTimeRemaining(null);
        }
    };

    const handleCloseQuizModal = () => {
        setQuizModalOpen(false);
        setShuffledQuiz(null); // Clean up shuffled quiz state
    };


    const handleCheckAnswer = () => {
        if (!shuffledQuiz || selectedAnswer === null) return;

        const currentQuestion = shuffledQuiz.questions[currentQuestionIndex];
        const isCorrect = selectedAnswer === currentQuestion.correct_answer_id;

        setAnswerStatus(isCorrect ? 'correct' : 'incorrect');
        setFinalAnswers(prev => ({
            ...prev,
            [currentQuestion.id]: selectedAnswer
        }));
    };

    const handleNextQuestion = () => {
        if (!shuffledQuiz) return;
        if (currentQuestionIndex < shuffledQuiz.questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
            setSelectedAnswer(null);
            setAnswerStatus('unanswered');
        }
    };

    const handleQuizSubmit = async () => {
        if (!selectedDocument?.quiz || !shuffledQuiz) return;

        const allAnswered = shuffledQuiz.questions.length === Object.keys(finalAnswers).length;
        if (!allAnswered) {
            alert('Please answer all questions');
            return;
        }

        setSubmittingQuiz(true);
        try {
            const timeTaken = quizStartTime
                ? Math.floor((Date.now() - quizStartTime) / 1000)
                : undefined;

            const result = await SubmitQuiz({
                quiz_id: selectedDocument.quiz.id,
                answers: Object.entries(finalAnswers).map(([questionId, answerId]) => ({
                    question: parseInt(questionId),
                    selected_answer: answerId
                })),
                time_taken_seconds: timeTaken
            });

            setQuizResult(result);

            const updatedDocs = await GetDocumentByCourseSlug(slug!);
            setDocuments(updatedDocs);
            const updatedDoc = updatedDocs.find(d => d.slug === selectedDocument.slug);
            if (updatedDoc) {
                setSelectedDocument(updatedDoc);
            }
        } catch (error: any) {
            alert(error.response?.data?.error || 'Quiz submission failed');
        } finally {
            setSubmittingQuiz(false);
            setTimeRemaining(null);
        }
    };

    const completedCount = documents.filter(d => d.user_progress?.is_completed).length;
    const totalDocs = documents.length;
    const courseProgress = totalDocs > 0 ? (completedCount / totalDocs) * 100 : 0;

    const DocumentsList = () => (
        <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Box sx={{p: 3, borderBottom: '1px solid', borderColor: 'divider'}}>
                <Typography level="title-lg" sx={{fontWeight: 700, mb: 2}}>
                    {course?.title}
                </Typography>

                <Box sx={{mb: 2}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
                        <Typography level="body-xs" fontWeight={600}>
                            Course Progress
                        </Typography>
                        <Typography level="body-xs" fontWeight={600}>
                            {completedCount}/{totalDocs}
                        </Typography>
                    </Box>
                    <LinearProgress
                        determinate
                        value={courseProgress}
                        sx={{height: 6, borderRadius: 'sm'}}
                    />
                </Box>

                <Input
                    placeholder="Search documents..."
                    size="sm"
                    startDecorator={<SearchIcon fontSize="small"/>}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{mt: 1}}
                />
            </Box>

            <Box sx={{flexGrow: 1, overflowY: 'auto', p: 2}}>
                <AccordionGroup
                    sx={{
                        [`& .${accordionSummaryClasses.indicator}`]: {
                            transition: '0.2s',
                        },
                        [`& [aria-expanded="true"] .${accordionSummaryClasses.indicator}`]: {
                            transform: 'rotate(45deg)',
                        },
                    }}
                >
                    {Object.keys(filteredGroupedDocuments).map((category) => (
                        <Accordion defaultExpanded={true} key={category}>
                            <AccordionSummary indicator={<AddIcon/>}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%'}}>
                                    <Typography level="title-sm" sx={{fontWeight: 700}}>
                                        {category}
                                    </Typography>
                                    <Chip size="sm" variant="soft">
                                        {filteredGroupedDocuments[category].length}
                                    </Chip>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                                    {filteredGroupedDocuments[category].map((document) => {
                                        const isSelected = selectedDocument?.slug === document.slug;
                                        const isCompleted = document.user_progress?.is_completed;

                                        return (
                                            // MOVE the onClick handler to this parent Box
                                            <Box
                                                key={document.slug}
                                                onClick={() => handleDocumentSelect(document)} // <--- MOVED HERE
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1.5,
                                                    borderRadius: 'sm',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    bgcolor: isSelected ? 'primary.softBg' : 'transparent',
                                                    border: '1px solid',
                                                    borderColor: isSelected ? 'primary.outlinedBorder' : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: isSelected ? 'primary.softBg' : 'background.level1',
                                                        borderColor: isSelected ? 'primary.outlinedBorder' : 'divider',
                                                    },
                                                }}
                                            >
                                                {isCompleted ? (
                                                    <CheckCircle
                                                        sx={{fontSize: 20, color: 'success.solidBg', flexShrink: 0}}/>
                                                ) : (
                                                    <Circle sx={{
                                                        fontSize: 20,
                                                        color: 'neutral.outlinedBorder',
                                                        flexShrink: 0
                                                    }}/>
                                                )}

                                                {/* REMOVE the onClick from this inner Box */}
                                                <Box sx={{flex: 1, minWidth: 0}}>
                                                    <Typography
                                                        level="body-sm"
                                                        sx={{
                                                            fontWeight: isSelected ? 600 : 400,
                                                            color: isSelected ? 'primary.plainColor' : 'text.primary',
                                                        }}
                                                    >
                                                        {document.title}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{display: 'flex', gap: 0.5, flexShrink: 0}}>
                                                    {document.youtube_link && (
                                                        <PlayCircleOutline
                                                            sx={{fontSize: 16, color: 'neutral.plainColor'}}/>
                                                    )}
                                                    {document.has_quiz && (
                                                        <QuizIcon sx={{fontSize: 16, color: 'primary.plainColor'}}/>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </AccordionGroup>

                {Object.keys(filteredGroupedDocuments).length === 0 && (
                    <Box sx={{textAlign: 'center', py: 4}}>
                        <Typography level="body-sm" color="neutral">
                            No documents found
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', mb: 3, gap: 2}}>
                <IconButton
                    variant="outlined"
                    size="sm"
                    sx={{display: {xs: 'flex', md: 'none'}}}
                    onClick={() => setDrawerOpen(true)}
                >
                    <MenuIcon/>
                </IconButton>
                <Breadcrumbs
                    size="sm"
                    aria-label="breadcrumbs"
                    separator={<ChevronRightRoundedIcon fontSize="small"/>}
                >
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home">
                        <HomeRoundedIcon/>
                    </Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>
                        Dashboard
                    </Link>
                    <Link underline="hover" color="neutral" href="/dashboard/knowledge-base" fontSize={12}
                          fontWeight={500}>
                        Knowledge Base
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        {course?.title}
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: {xs: 'column', md: 'row'},
                    gap: 3,
                    minHeight: 0,
                    flexGrow: 1,
                }}
            >
                <Drawer
                    anchor="left"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    sx={{display: {xs: 'block', md: 'none'}}}
                >
                    <Sheet sx={{width: 320, height: '100%'}}>
                        <DocumentsList/>
                    </Sheet>
                </Drawer>

                <Card
                    sx={{
                        display: {xs: 'none', md: 'flex'},
                        width: 360,
                        flexShrink: 0,
                        p: 0,
                        height: '100%',
                        position: 'sticky',
                        top: 100,
                    }}
                    variant="outlined"
                >
                    <DocumentsList/>
                </Card>

                <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: 3}}>
                    {selectedDocument && scrollProgress > 0 && (
                        <LinearProgress
                            determinate
                            value={scrollProgress}
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1000,
                                height: 3,
                                borderRadius: 0,
                            }}
                        />
                    )}

                    <Card
                        sx={{
                            gap: 0,
                            flex: 1,
                            p: 0,
                            minHeight: 'calc(100vh - 200px)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        variant="outlined"
                    >
                        {selectedDocument ? (
                            <>
                                <Box
                                    id="content-card"
                                    sx={{
                                        flex: 1,
                                        overflowY: 'auto',
                                        minHeight: 0
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: {xs: 3, md: 5},
                                            pb: 3,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'background.level1',
                                        }}
                                    >
                                        <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap'}}>
                                            <Chip size="sm" variant="soft" color="primary">
                                                {selectedDocument.category}
                                            </Chip>
                                            {selectedDocument.youtube_link && (
                                                <Chip size="sm" variant="soft" color="success"
                                                      startDecorator={<PlayCircleOutline/>}>
                                                    Video Included
                                                </Chip>
                                            )}
                                            {selectedDocument.has_quiz && (
                                                <Chip size="sm" variant="soft" color="warning"
                                                      startDecorator={<QuizIcon/>}>
                                                    Quiz Required
                                                </Chip>
                                            )}
                                            {selectedDocument.user_progress?.is_completed && (
                                                <Chip size="sm" variant="soft" color="success"
                                                      startDecorator={<CheckCircle/>}>
                                                    Completed
                                                </Chip>
                                            )}
                                        </Box>

                                        <Typography level="h2" sx={{mb: 1, fontWeight: 700}}>
                                            {selectedDocument.title}
                                        </Typography>
                                        <Typography level="body-sm"
                                                    sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                            <Box component="span" sx={{fontWeight: 600}}>Author:</Box>
                                            {selectedDocument.author}
                                        </Typography>
                                    </Box>

                                    {selectedDocument.youtube_link && (
                                        <Box sx={{p: {xs: 3, md: 5}, bgcolor: 'background.surface'}}>
                                            <YouTubeEmbed videoId={selectedDocument.youtube_link}/>
                                        </Box>
                                    )}

                                    {selectedDocument.links && selectedDocument.links.length > 0 && (
                                        <Box sx={{p: {xs: 3, md: 5}, bgcolor: 'background.surface'}}>
                                            <Card
                                                variant="soft"
                                                sx={{
                                                    p: 3,
                                                    borderLeft: '4px solid',
                                                    borderColor: 'primary.solidBg',
                                                }}
                                            >
                                                <Typography level="title-lg" sx={{mb: 2, fontWeight: 700}}>
                                                    📚 Key Resources
                                                </Typography>
                                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                                                    {selectedDocument.links.map((link, index) => (
                                                        <Link
                                                            key={index}
                                                            href={link.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            underline="none"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                p: 2,
                                                                borderRadius: 'md',
                                                                bgcolor: 'background.surface',
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    borderColor: 'primary.outlinedBorder',
                                                                    boxShadow: 'sm',
                                                                    transform: 'translateX(4px)',
                                                                },
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    p: 1,
                                                                    borderRadius: 'sm',
                                                                    bgcolor: 'primary.softBg',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <OpenInNew
                                                                    sx={{fontSize: 20, color: 'primary.solidBg'}}/>
                                                            </Box>
                                                            <Typography level="body-sm"
                                                                        sx={{fontWeight: 500, flex: 1}}>
                                                                {link.title}
                                                            </Typography>
                                                            <ArrowRightAltSharp sx={{color: 'neutral.plainColor'}}/>
                                                        </Link>
                                                    ))}
                                                </Box>
                                            </Card>
                                        </Box>
                                    )}

                                    <Box
                                        sx={{
                                            background: 'white',
                                            p: {xs: 3, md: 5},
                                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                mt: 3,
                                                mb: 2,
                                                fontWeight: 600,
                                            },
                                            '& p': {
                                                mb: 2,
                                                lineHeight: 1.8,
                                            },
                                            '& ul, & ol': {
                                                mb: 2,
                                                pl: 3,
                                            },
                                            '& li': {
                                                mb: 1,
                                            },
                                        }}
                                        dangerouslySetInnerHTML={{__html: selectedDocument.content}}
                                    />
                                </Box>

                                <Box sx={{
                                    p: {xs: 2, md: 3},
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.surface',
                                    flexShrink: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2
                                }}>
                                    {selectedDocument.has_quiz && (
                                        <Button
                                            size="lg"
                                            onClick={handleStartQuiz}
                                            startDecorator={<QuizIcon/>}
                                            fullWidth
                                            disabled={selectedDocument.user_progress?.is_completed && selectedDocument.requires_quiz}
                                        >
                                            {selectedDocument.user_progress?.is_completed
                                                ? 'Quiz Completed'
                                                : 'Take Quiz'}
                                        </Button>
                                    )}

                                    {!selectedDocument.requires_quiz && (
                                        <Button
                                            size="lg"
                                            variant="soft"
                                            onClick={handleToggleComplete}
                                            startDecorator={
                                                selectedDocument.user_progress?.is_completed
                                                    ? <CheckCircle/>
                                                    : <Circle/>
                                            }
                                            fullWidth
                                        >
                                            {selectedDocument.user_progress?.is_completed
                                                ? 'Mark as Incomplete'
                                                : 'Mark as Complete'}
                                        </Button>
                                    )}
                                </Box>
                            </>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    textAlign: 'center',
                                    p: 5,
                                }}
                            >
                                <Box>
                                    <Typography level="h3" sx={{mb: 2, fontWeight: 700}}>
                                        Select a Document
                                    </Typography>
                                    <Typography level="body-md" color="neutral" sx={{mb: 3}}>
                                        Choose a document from the sidebar to begin
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Card>
                </Box>
            </Box>

            <Modal open={quizModalOpen} onClose={handleCloseQuizModal}>
                <ModalDialog sx={{maxWidth: 800, width: '100%', maxHeight: '90vh', overflow: 'auto'}}>
                    {!quizResult ? (
                        <>
                            {shuffledQuiz && (() => {
                                const questions = shuffledQuiz.questions;
                                const currentQuestion = questions[currentQuestionIndex];
                                const isLastQuestion = currentQuestionIndex === questions.length - 1;

                                return (
                                    <Box>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 2
                                        }}>
                                            <Typography level="h3">
                                                {shuffledQuiz.title}
                                            </Typography>
                                            {timeRemaining !== null && (
                                                <Chip color={timeRemaining < 60 ? 'danger' : 'primary'}
                                                      variant="soft">
                                                    ⏱️ {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
                                                </Chip>
                                            )}
                                        </Box>

                                        <Box sx={{mb: 3}}>
                                            <LinearProgress
                                                determinate
                                                value={((currentQuestionIndex + 1) / questions.length) * 100}
                                            />
                                            <Typography level="body-xs" sx={{textAlign: 'right', mt: 0.5}}>
                                                Question {currentQuestionIndex + 1} of {questions.length}
                                            </Typography>
                                        </Box>

                                        <Card key={currentQuestion.id} variant="outlined" sx={{mb: 3, p: 3}}>
                                            <Typography level="title-md" sx={{mb: 2}}>
                                                {currentQuestion.question_text}
                                            </Typography>
                                            <RadioGroup
                                                value={selectedAnswer}
                                                onChange={(e) => setSelectedAnswer(parseInt(e.target.value))}
                                            >
                                                {currentQuestion.answers.map((answer) => (
                                                    <Radio
                                                        key={answer.id}
                                                        value={answer.id}
                                                        label={answer.answer_text}
                                                        disabled={answerStatus !== 'unanswered'}
                                                        sx={{mb: 1}}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </Card>

                                        {answerStatus !== 'unanswered' && (
                                            <Alert
                                                color={answerStatus === 'correct' ? 'success' : 'danger'}
                                                variant="soft"
                                                sx={{mb: 3, display: 'flex', flexDirection: 'column'}}
                                            >
                                                <Typography level="title-md" sx={{mb: 1}}>
                                                    {answerStatus === 'correct' ? 'Correct!' : 'Incorrect'}
                                                </Typography>
                                                {currentQuestion.helper_text && (
                                                    <Typography level="body-sm">
                                                        {currentQuestion.helper_text}
                                                    </Typography>
                                                )}
                                            </Alert>
                                        )}

                                        {answerStatus === 'unanswered' ? (
                                            <Button
                                                onClick={handleCheckAnswer}
                                                disabled={selectedAnswer === null}
                                                fullWidth
                                                size="lg"
                                            >
                                                Submit Answer
                                            </Button>
                                        ) : isLastQuestion ? (
                                            <Button
                                                onClick={handleQuizSubmit}
                                                loading={submittingQuiz}
                                                fullWidth
                                                size="lg"
                                                color="success"
                                            >
                                                Finish Quiz & See Results
                                            </Button>
                                        ) : (
                                            <Button onClick={handleNextQuestion} fullWidth size="lg">
                                                Next Question
                                            </Button>
                                        )}
                                    </Box>
                                );
                            })()}
                        </>
                    ) : (
                        <Box sx={{textAlign: 'center', p: 3}}>
                            <Alert color={quizResult.passed ? 'success' : 'warning'}
                                   sx={{mb: 3, justifyContent: 'center'}}>
                                {quizResult.passed ? '🎉 Congratulations - You Passed!' : 'Keep Learning and try again'}
                            </Alert>
                            <Typography level="h2" sx={{mb: 2}}>
                                {quizResult.score_percentage}%
                            </Typography>
                            <Typography level="body-md" sx={{mb: 1}}>
                                {quizResult.earned_points} / {quizResult.total_points} points
                            </Typography>
                            <Typography level="body-sm" color="neutral" sx={{mb: 3}}>
                                Passing: {selectedDocument?.quiz?.passing_percentage}%
                            </Typography>
                            <Button onClick={handleCloseQuizModal} fullWidth>
                                {quizResult.passed ? 'Continue Learning' : 'Close'}
                            </Button>
                        </Box>
                    )}
                </ModalDialog>
            </Modal>
        </>
    )
}